import { useSelector } from 'react-redux'
import { UploadFlow } from '@vp/upload-flow'
import { v4 as uuid } from 'uuid'

import { ANALYTICS_CATEGORY, ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL } from '~/shared/constants'
import { instrumenter } from '~/app/utils/instrumentation'
import {
  getProductKey,
  getProductVersion,
  getLocaleSelector,
  getQuantitySelector,
  getGalleryNameSelector,
  getMpvid,
  getTenant,
  getH1Title,
} from '~/app/store/config'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { userOptionsSelector } from '~/app/store/refinement/selectors'
import { Tile } from '~/app/components/Tile'
import { UploadTile } from './UploadTile'
import { useIdentity } from '@vp/ubik-context'
import { usePricingContext } from '~/app/features/FragmentRoot/contexts/pricing/context'

export const FullBleedUploadTile = ({ userInteractionLocation }: Gallery.Analytics.ButtonLocationProp) => {
  /**
   * Selectors
   */
  const analytics = useAnalytics()

  const tenant = useSelector(getTenant)
  const locale = useSelector(getLocaleSelector)
  const mpvId = useSelector(getMpvid)
  const quantity = useSelector(getQuantitySelector)
  const productKey = useSelector(getProductKey)
  const name = useSelector(getGalleryNameSelector)
  const title = useSelector(getH1Title)
  const userOptions = useSelector(userOptionsSelector)
  const productVersion = useSelector(getProductVersion)

  const pricingContext = usePricingContext()
  const retrievedPricingContext = pricingContext?.getPricingContext()
  const vatInclusivity = retrievedPricingContext?.vatInclusive || false
  const pricingContextString = pricingContext?.getEncodedContextString()
  const useFullPath = true
  const { pageSection, pageStage, pageName } = analytics.getPageProperties()

  /**
   * Handlers
   */
  const handlePopupOpen = (selections: Record<string, string>): void => {
    analytics.trackEvent({
      userInteractionLocation,
      action: ANALYTICS_EVENT_ACTIONS.DESIGN_POPUP_DISPLAYED,
      category: ANALYTICS_CATEGORY.DESIGN_UPLOAD,
      eventLabel: ANALYTICS_LABEL.OPTIONS_DISPLAYED,
      ...analytics.buildFullBleedUploadMetaData(selections),
    })
  }

  const { identity } = useIdentity()

  const handleSubmit = (selections: Record<string, string>): void => {
    instrumenter.recordDesignSelection(selections, 'fbu')

    const fullBleedEngagementId = uuid()

    analytics.trackEvent({
      userInteractionLocation,
      action: ANALYTICS_EVENT_ACTIONS.DESIGN_POPUP_DISPLAYED,
      category: ANALYTICS_CATEGORY.DESIGN_UPLOAD,
      eventLabel: ANALYTICS_LABEL.OPTIONS_VALIDATION,
      fullbleedEngagementId: fullBleedEngagementId,
      ...analytics.buildFullBleedUploadMetaData(selections),
    })
  }

  return (
    <Tile className="full-bleed-upload-tile" type="fbu">
      <UploadFlow
        analyticsPageData={{
          pageSection,
          pageStage,
          pageName,
          productName: name,
          coreProductId: productKey,
          position: 0,
        }}
        authToken={identity?.authorizationHeader ?? undefined}
        loader={<UploadTile />}
        locale={locale}
        mpvId={mpvId}
        pricingContextString={pricingContextString ?? ''}
        productKey={productKey}
        productName={title}
        productVersion={productVersion ?? 0}
        quantity={quantity as number}
        requestor="gallery6-client"
        tenant={tenant}
        useFullPath={useFullPath}
        userOptions={userOptions as Record<string, string>}
        vatInc={vatInclusivity}
        onPopupOpen={handlePopupOpen}
        onSubmit={handleSubmit}
      >
        <UploadTile />
      </UploadFlow>
    </Tile>
  )
}

FullBleedUploadTile.displayName = 'FullBleedUploadTile'
